import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

import { Header } from 'components/Header';
import { useServiceConfigs } from '../context/ServiceConfigContext';
import { getCustomerInfo, getServiceConfigs } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { CustomerInfo, ServiceConfig } from '../services/types';
import { isGoldFromAttributes } from '../utils/metal';
import { safeCopy } from '../helpers/safeCopy';
import { DEFAULT_SERVICE_ICON, getServiceIconAsset } from '../utils/serviceIcons';
import { sortServiceConfigs } from '../utils/serviceConfig';

interface ServiceOptionsProps {
  storeId: string;
  externalIdentity: string;
  cartId: string;
}

type NormalizedLocation = {
  id: string;
  label: string;
  icon: React.ReactNode;
  colorKey: 'info' | 'primary';
};

const splitLocations = (locations: string[] = []): string[] =>
  locations
    .flatMap((loc) => String(loc).split(',').map((value) => value.trim()))
    .filter(Boolean);

const hasProductCareLocation = (locations: string[] = []): boolean =>
  splitLocations(locations).some((value) => /Kerrville/i.test(value) || /Product\s*Care/i.test(value));

const productCareLocations = (locations: string[] = []): NormalizedLocation[] => {
  if (!hasProductCareLocation(locations)) {
    return [];
  }
  return [
    {
      id: 'product-care',
      label: 'Product Care',
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
      colorKey: 'info',
    },
  ];
};

const ServiceTile: React.FC<{
  config: ServiceConfig;
  onSelect: () => void;
}> = ({ config, onSelect }) => {
  const { description, cost, locationServiced, allowQuantity, icon } = config;
  const locations = useMemo(() => productCareLocations(locationServiced), [locationServiced]);
  const iconSrc = useMemo(() => getServiceIconAsset(icon), [icon]);
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        },
      })}
      onClick={onSelect}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={(theme) => ({
              width: 48,
              height: 48,
              borderRadius: '0 0 16px 0',
              bgcolor: theme.palette.grey[50],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: `0.8px solid ${alpha(theme.palette.divider, 0.6)}`,
              overflow: 'hidden',
            })}
          >
            <Box
              component="img"
              src={iconSrc}
              alt={`${description} icon`}
              sx={{ width: 28, height: 28 }}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = DEFAULT_SERVICE_ICON;
              }}
            />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {description}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`$${cost.toFixed(2)}`} color="primary" variant="outlined" />
          {allowQuantity && (
            <Chip
              size="small"
              icon={<NumbersOutlinedIcon sx={{ fontSize: 16 }} />}
              label="Quantity available"
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          )}
        </Stack>
        {!!locations.length && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {locations.map((loc) => (
              <Chip
                key={loc.id}
                size="small"
                label={loc.label}
                sx={(theme) => ({
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette[loc.colorKey].main, 0.12),
                  color: theme.palette[loc.colorKey].main,
                  '& .MuiChip-icon': {
                    color: theme.palette[loc.colorKey].main,
                  },
                })}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const ServiceOptionsPage: React.FC<ServiceOptionsProps> = ({ storeId, externalIdentity, cartId }) => {
  const {
    configs,
    setConfigs,
    customer,
    setCustomer,
    productId,
    extendedAttributes,
    selectedServices,
  } = useServiceConfigs();
  const navigate = useNavigate();

  const [visibleConfigs, setVisibleConfigs] = useState<ServiceConfig[]>([]);
  const [loading, setLoading] = useState(!configs || configs.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');

  const isGold = useMemo(
    () => isGoldFromAttributes(extendedAttributes, productId),
    [extendedAttributes, productId]
  );

  const shortenId = (id: string, start = 8, end = 4) =>
    id && id.length > start + end ? `${id.slice(0, start)}…${id.slice(-end)}` : id;

  const filterConfigsForProductCare = useCallback(
    (items: ServiceConfig[] = []): ServiceConfig[] =>
      sortServiceConfigs(
        items.filter((cfg) => {
          const metalMatches = cfg.metal === 'Both' || cfg.metal === (isGold ? 'Gold' : 'Non-Gold');
          return metalMatches && hasProductCareLocation(cfg.locationServiced ?? []);
        }),
      ),
    [isGold]
  );

  useEffect(() => {
    if (!externalIdentity || !storeId) {
      setError('Missing authentication context');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        if (!configs || !customer) {
          const customerData: CustomerInfo = await getCustomerInfo(externalIdentity, cartId);
          if (!customerData?.id) {
            setError('Please add the customer to the cart before proceeding.');
            return;
          }
          setCustomer(customerData);

          const allConfigs = await getServiceConfigs(externalIdentity);
          const ordered = sortServiceConfigs(allConfigs);
          setConfigs(ordered);
          setVisibleConfigs(filterConfigsForProductCare(ordered));
        } else {
          setVisibleConfigs(filterConfigsForProductCare(configs));
        }
      } catch (err: any) {
        logApiError('Loading service options', err);
        setError(formatApiErrorMessage(err, 'Failed to load service options'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    externalIdentity,
    cartId,
    storeId,
    configs,
    customer,
    filterConfigsForProductCare,
    setConfigs,
    setCustomer,
  ]);

  const filteredConfigs = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) return visibleConfigs;
    return visibleConfigs.filter((cfg) => cfg.description.toLowerCase().includes(query));
  }, [searchInput, visibleConfigs]);

  const handleClearSearch = () => {
    setSearchInput('');
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Alert severity="warning" variant="outlined" sx={{ width: '100%', maxWidth: 480 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Service Options</Typography>}
        subtitle="Select a service to continue"
        align="left"
        sticky
        elevateOnScroll
        serviceCount={selectedServices.length}
        onBadgeClick={() => navigate('/summary')}
        rightSlot={<InfoOutlinedIcon sx={{ opacity: 0.6 }} />}
      />

      {customer && (
        <Card
          sx={(theme) => ({
            mx: 2,
            mt: 2,
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          })}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 2,
              py: 1.25,
              background:
                theme.palette.mode === 'light'
                  ? alpha(theme.palette.primary.main, 0.06)
                  : alpha(theme.palette.primary.main, 0.18),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            })}
          >
            <Avatar
              variant="rounded"
              sx={(theme) => ({
                width: 36,
                height: 36,
                borderRadius: 1.2,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
              })}
            >
              <PersonOutlineIcon fontSize="small" />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {customer.firstName} {customer.lastName}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              label={shortenId(customer.id || '')}
              title={`${customer.id || ''}\nClick to copy`}
              onClick={async () => {
                const ok = await safeCopy(customer.id || '');
                setSnack(ok ? 'Customer ID copied' : 'Copy failed');
              }}
              sx={{ ml: 'auto', borderRadius: 2, cursor: 'pointer' }}
            />
          </Box>

          <CardContent sx={{ py: 2 }}>
            <Stack spacing={1.25} divider={<Box component="span" sx={{ borderBottom: `1px solid ${alpha('#000', 0.08)}` }} />}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={1}>
                  <EmailOutlinedIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                </Stack>
                <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>
                  {customer.email || '—'}
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={1}>
                  <PhoneIphoneOutlinedIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                </Stack>
                <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>
                  {customer.phone || '—'}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Paper
        elevation={0}
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          mb: 4,
          p: 2,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          background: alpha(theme.palette.primary.light, 0.05),
          backdropFilter: 'saturate(120%) blur(4px)',
        })}
      >
        <Typography variant="h6" gutterBottom>
          Available services
        </Typography>

        <Box mb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by service description"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ opacity: 0.7 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchInput ? (
                    <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {filteredConfigs.length ? (
          <Grid container spacing={2}>
            {filteredConfigs.map((config) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={config.id}>
                <ServiceTile config={config} onSelect={() => navigate(`/services/details/${config.id}`)} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box py={4} textAlign="center">
            <Typography variant="subtitle2" gutterBottom>
              {visibleConfigs.length ? 'No services match your search.' : 'No services available.'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check back later or contact support.
            </Typography>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={Boolean(snack)}
        message={snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
      />
    </Box>
  );
};

export default ServiceOptionsPage;
